import React, { useMemo } from "react"
import { Link } from "gatsby"
import {
  BriefcaseIcon,
  ChevronRightIcon,
  ProductsIcon,
  ProfileIcon,
  StockLevelsIcon,
  CheckCircleOutlineIcon,
  CheckCircleIcon
} from "components/Icons"
import Show from "components/Show"
import useOnboardingTasks, { TaskType } from "hooks/useOnboardingTasks"
import OnboardingStep from "./OnboardingStep"
import useDesignerUser from "hooks/useDesignerUser"
import Alert from "components/Alert"
import { track } from "lib/segment"
import FeatureFlagComponentGuard from "components/FeatureFlagComponentGuard"

const OnboardingSteps = () => {
  const { tasks } = useOnboardingTasks()
  const { designerUser } = useDesignerUser()

  const completedTasks = useMemo<Record<TaskType, boolean>>(
    () =>
      tasks?.reduce(
        (item: Record<TaskType, boolean>, task) => ({
          ...item,
          [task.type]: task.isComplete
        }),
        {} as Record<TaskType, boolean>
      ) || ({} as Record<TaskType, boolean>),
    [tasks]
  )

  const completedIcon = (
    <CheckCircleIcon
      className="w-4 h-4 ml-2.5 text-[#44692B] [&>path]:fill-current"
      aria-label="Completed"
    />
  )

  const incompleteIcon = (
    <CheckCircleOutlineIcon
      className="w-4 h-4 ml-2.5 text-[#B8B8B8] [&>circle]:fill-current"
      aria-label="Incomplete"
    />
  )

  return (
    <div
      className="bg-white border border-primary-light-gray rounded-lg p-4"
      data-testid="onboarding-task"
    >
      <h2 className="heading-4 mb-2">Onboarding tasks</h2>

      <p className="text-primary-dark-gray mb-6">
        Complete these quick tasks to get started and make the most of the
        platform.
      </p>

      <ul className="grid grid-cols-1 gap-2">
        <OnboardingStep icon={ProfileIcon}>
          <a
            href="/dashboard/designer/profile/"
            className="flex justify-between items-center"
          >
            <h3 className="font-medium inline-flex items-center">
              Complete profile
              <Show when={completedTasks?.profile} fallback={incompleteIcon}>
                {completedIcon}
              </Show>
            </h3>

            <ChevronRightIcon className="w-6 h-6" aria-hidden="true" />
          </a>
        </OnboardingStep>

        <OnboardingStep icon={ProductsIcon}>
          <Link
            to="/products/"
            className="flex justify-between items-center"
            data-testid="add-first-product"
          >
            <h3 className="font-medium inline-flex items-center">
              Add your first product
              <Show when={completedTasks?.product} fallback={incompleteIcon}>
                {completedIcon}
              </Show>
            </h3>

            <ChevronRightIcon className="w-6 h-6" aria-hidden="true" />
          </Link>
        </OnboardingStep>

        <OnboardingStep icon={StockLevelsIcon}>
          <Link
            to="/stock/"
            className="flex justify-between items-center"
            data-testid="update-stock-level"
          >
            <h3 className="font-medium inline-flex items-center">
              Update your stock levels
              <Show when={completedTasks?.stock} fallback={incompleteIcon}>
                {completedIcon}
              </Show>
            </h3>

            <ChevronRightIcon className="w-6 h-6" aria-hidden="true" />
          </Link>
        </OnboardingStep>

        <OnboardingStep icon={BriefcaseIcon}>
          <a
            href="/dashboard/designer/account/"
            className="flex justify-between items-center"
            data-testid="account-details"
          >
            <h3 className="font-medium inline-flex items-center">
              Account details
              <Show when={completedTasks?.account} fallback={incompleteIcon}>
                {completedIcon}
              </Show>
            </h3>

            <ChevronRightIcon className="w-6 h-6 m-[1px]" aria-hidden="true" />
          </a>
        </OnboardingStep>

        <FeatureFlagComponentGuard feature="feature_brands_payout_ui">
          <Show when={designerUser}>
            <li>
              <Alert
                title="Add payment details"
                status="warning"
                cta={
                  <Link
                    to="/payment/"
                    onClick={() =>
                      track(`Homepage Payout Form Clicked`, {
                        brand_id: designerUser?.designer?.id
                      })
                    }
                  >
                    Add payment details
                  </Link>
                }
              >
                If you haven&apos;t done so already, please provide us with your
                bank details to ensure that you get paid for the orders you
                receive on the platform.
              </Alert>
            </li>
          </Show>
        </FeatureFlagComponentGuard>

        <FeatureFlagComponentGuard feature="feature_brands_payout_ui" invert>
          <Show when={designerUser}>
            <li>
              <Alert
                title="Add payment details"
                status="warning"
                cta={
                  <a
                    href="https://docs.google.com/forms/d/e/1FAIpQLSeUfiYc-PSe8DpGBy6ZcXi-OT7T7C7LH9yPpuwj6iFEO_vGSg/viewform"
                    target="_blank"
                    data-testid="old-add-payment-details"
                    rel="noreferrer"
                    onClick={() =>
                      track(`Homepage Payout Form Clicked`, {
                        brand_id: designerUser?.designer?.id
                      })
                    }
                  >
                    Add payment details
                  </a>
                }
              >
                If you haven&apos;t done so already, please provide us with your
                bank details to ensure that you get paid for the orders you
                receive on the platform.
              </Alert>
            </li>
          </Show>
        </FeatureFlagComponentGuard>
      </ul>
    </div>
  )
}

export default OnboardingSteps
